<template>
  <main class="ws">

    <!-- ============ 首屏 ============ -->
    <section class="ph ph--gray ph--center">
      <div class="wrap">
        <span class="ph__kicker">{{ $t("帮助中心") }}</span>
        <h1 class="ph__title">{{ $t("有问题，先在这里找") }}</h1>
        <p class="ph__sub">{{ $t("常见问题按分类整理好了，搜不到就直接问 AI 客服，它会带着你的问题接着答。") }}</p>

        <div class="hsearch">
          <svg class="ico"><use href="#i-search"/></svg>
          <input
            type="search"
            v-model="q"
            :placeholder="$t('搜索：怎么改价格、退款怎么处理、忘记密码…')"
            @keyup.enter="doSearch"
          >
          <button type="button" @click="doSearch">{{ $t("搜索") }}</button>
        </div>
        <p class="hsearch__hot">
          {{ $t("常搜：") }}<a href="#faq" v-for="(w, i) in hotWords" :key="i" @click.prevent="pickHot(w)">{{ w }}</a>
        </p>

        <!-- 搜索之外的第二条路：直接问 AI -->
        <div class="hsearch__ai">
          <span class="hsearch__ai-ico"><svg class="ico"><use href="#i-chat"/></svg></span>
          <div class="hsearch__ai-txt">
            <b>{{ $t("懒得翻？直接问 AI 客服") }}</b>
            <em>{{ $t("开店、选品、物流、结算都能问，答不上会转人工。") }}</em>
          </div>
          <button type="button" class="hsearch__ai-btn" @click="askAi(q)">{{ $t("开始提问") }}</button>
        </div>

        <div class="guide guide--center">
          <img class="guide__m" src="/website/img/tao-think.png" alt="">
          <div class="bubble">{{ $t("搜不到就直接问 AI 客服，") }}<b>{{ $t("别自己在那儿猜。") }}</b>{{ $t("猜错一步，后面要多改三处。") }}</div>
        </div>
      </div>
    </section>

    <!-- ============ 分类 ============ -->
    <section class="sec sec--tight">
      <div class="wrap">
        <div class="grid4" id="start">

          <a
            class="hcat"
            :class="{ 'is-on': cat === c.key }"
            href="#faq"
            v-for="c in cats"
            :key="c.key"
            @click.prevent="pickCat(c.key)"
          >
            <span class="ico-badge ico-badge--sm"><svg class="ico"><use :href="c.icon"/></svg></span>
            <div class="hcat__t">{{ $t(c.name) }}</div>
            <div class="hcat__d">{{ $t(c.desc) }}</div>
            <div class="hcat__n">{{ countOf(c.key) }} {{ $t("条") }}</div>
          </a>

        </div>
      </div>
    </section>

    <!-- ============ 常见问题 ============ -->
    <section class="sec sec--gray">
      <div class="wrap">
        <div class="sec__head">
          <span class="eyebrow">{{ $t("常见问题") }}</span>
          <h2 class="sec__title">{{ cat ? $t(catName) : $t("大家问得最多的这些") }}</h2>
        </div>

        <div class="hfaq__bar" v-if="cat || q">
          <span>{{ $t("当前筛选：") }}{{ cat ? $t(catName) : '' }}{{ cat && q ? ' · ' : '' }}{{ q }}</span>
          <button type="button" @click="resetFilter">{{ $t("清空筛选") }}</button>
        </div>

        <div class="faq" style="max-width:880px;margin:0 auto" id="faq" ref="faq">
          <details v-for="(f, i) in filtered" :key="f.q" :open="i === 0">
            <summary>{{ $t(f.q) }}</summary>
            <div class="faq__a">
              {{ $t(f.a) }}
              <button type="button" class="hfaq__ask" @click="askAi(f.q)">
                <svg class="ico"><use href="#i-chat"/></svg>{{ $t("这条没解决，问 AI 客服") }}
              </button>
            </div>
          </details>

          <!-- 搜索没命中：直接把问题交给 AI，不让访客卡在空页面上 -->
          <div v-if="!filtered.length" class="hfaq__empty">
            <p>{{ $t("没有找到这一条。") }}</p>
            <button type="button" class="btn btn--primary" @click="askAi(q)">
              {{ $t("把这个问给 AI 客服") }}
            </button>
          </div>
        </div>

        <p v-if="asked && hits === 0" class="hfaq__sent">
          {{ $t("没搜到，已经把「") }}{{ asked }}{{ $t("」发给 AI 客服了，在右下角继续聊。") }}
        </p>
      </div>
    </section>

    <!-- ============ 三种求助方式 ============ -->
    <section class="sec" id="contact">
      <div class="wrap">
        <div class="sec__head">
          <span class="eyebrow">{{ $t("还没解决") }}</span>
          <h2 class="sec__title">{{ $t("三条路，总有一条能通") }}</h2>
          <p class="sec__sub">{{ $t("AI 客服随时在线，工单最慢 4 小时回，邮件适合附合同和证件。") }}</p>
        </div>

        <div class="grid3">
          <article class="card">
            <span class="ico-badge"><svg class="ico"><use href="#i-chat"/></svg></span>
            <h3 class="card__t">{{ $t("AI 客服") }}</h3>
            <p class="card__d">{{ $t("开店、选品、物流、结算的常见问题，它当场答；答不上会转人工。") }}</p>
            <a class="frow__link" href="#contact" @click.prevent="askAi('')">{{ $t("打开对话") }}<svg class="ico"><use href="#i-arrow"/></svg></a>
          </article>
          <article class="card">
            <span class="ico-badge"><svg class="ico"><use href="#i-doc"/></svg></span>
            <h3 class="card__t">{{ $t("提工单") }}</h3>
            <p class="card__d">{{ $t("描述清楚问题、附上订单号或截图，一般 4 小时内有人接。") }}</p>
            <router-link class="frow__link" to="/sign-in">{{ $t("登录后提工单") }}<svg class="ico"><use href="#i-arrow"/></svg></router-link>
          </article>
          <article class="card">
            <span class="ico-badge"><svg class="ico"><use href="#i-mail"/></svg></span>
            <h3 class="card__t">{{ $t("发邮件") }}</h3>
            <p class="card__d">{{ $t("适合需要附合同、发票、证件的情况，附件不会丢。") }}</p>
            <a class="frow__link" href="mailto:liutaotao@taocarts.com">{{ $t("写邮件") }}<svg class="ico"><use href="#i-arrow"/></svg></a>
          </article>
        </div>
      </div>
    </section>

    <!-- ============ 开放接口 ============ -->
    <section class="sec sec--gray" id="api">
      <div class="wrap">
        <div class="frow">
          <div>
            <span class="frow__tag">{{ $t("开放接口") }}</span>
            <h2 class="frow__t">{{ $t("有自己的系统？接进来就行") }}</h2>
            <p class="frow__d">{{ $t("商品、库存、订单、物流轨迹都开放了 REST 接口和 Webhook。用 ERP 管货的，可以只同步库存；用自己后台管订单的，可以只拉订单。") }}</p>
            <ul class="frow__list">
              <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("REST API，Token 鉴权，支持分页与增量拉取") }}</span></li>
              <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("Webhook 推送下单、付款、发货、退款事件") }}</span></li>
              <li><svg class="ico"><use href="#i-check"/></svg><span>{{ $t("沙箱环境，不影响正式店铺数据") }}</span></li>
            </ul>
            <a class="frow__link" href="#api" @click.prevent="askAi('我想对接开放接口，能发一份接口文档吗')">{{ $t("向 AI 客服要接口文档") }}<svg class="ico"><use href="#i-arrow"/></svg></a>
          </div>
          <div class="frow__media">
            <div class="media-card">
              <pre style="margin:0;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:12.5px;line-height:1.85;color:#3B3552;overflow-x:auto"><span style="color:#8B84A3">POST</span> /openapi/v1/orders
<span style="color:#8B84A3">Authorization:</span> Bearer &lt;token&gt;

{
  <span style="color:#7331FF">"shop_id"</span>: 1024,
  <span style="color:#7331FF">"status"</span>: <span style="color:#2FA96B">"paid"</span>,
  <span style="color:#7331FF">"currency"</span>: <span style="color:#2FA96B">"USD"</span>,
  <span style="color:#7331FF">"total"</span>: 76.00,
  <span style="color:#7331FF">"items"</span>: [
    { <span style="color:#7331FF">"sku"</span>: <span style="color:#2FA96B">"TF-TEE-01"</span>, <span style="color:#7331FF">"qty"</span>: 2 }
  ]
}</pre>
              <div class="note" style="margin-top:14px">
                <svg class="ico"><use href="#i-lock"/></svg>
                <span>{{ $t("接口按") }}<b>{{ $t("店铺维度隔离，") }}</b>{{ $t("一个 Token 只能读自己店的数据。") }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ============ 服务状态 ============ -->
    <section class="sec sec--tight" id="status">
      <div class="wrap">
        <div class="metrics">
          <div class="metric"><b style="color:#2FA96B;font-size:22px">●&nbsp;{{ $t("正常") }}</b><span>{{ $t("店铺前台访问") }}</span></div>
          <div class="metric"><b style="color:#2FA96B;font-size:22px">●&nbsp;{{ $t("正常") }}</b><span>{{ $t("后台与订单接口") }}</span></div>
          <div class="metric"><b style="color:#2FA96B;font-size:22px">●&nbsp;{{ $t("正常") }}</b><span>{{ $t("面单与轨迹回传") }}</span></div>
          <div class="metric"><b style="color:#2FA96B;font-size:22px">●&nbsp;{{ $t("正常") }}</b><span>{{ $t("AI 能力") }}</span></div>
        </div>
        <p class="plans__note">{{ $t("最近一次异常：10-21 02:10–02:26，物流轨迹回传延迟，已恢复。") }}<a href="#status" style="color:var(--p)" @click.prevent="askAi('想了解一下平台最近的服务状态和异常记录')">{{ $t("查看历史") }}</a></p>
      </div>
    </section>

    <!-- ============ CTA ============ -->
    <section class="sec sec--tight">
      <div class="wrap">
        <div class="pandy">
          <img class="pandy__m" src="/website/img/tao-think.png" alt="">
          <div>
            <p class="pandy__t">{{ $t("还是没解决？直接问 AI 客服") }}</p>
            <p class="pandy__b">{{ $t("别自己在那儿猜。") }}<b>{{ $t("猜错一步，后面要多改三处。") }}</b>{{ $t("把店铺编号和截图一起发过去，通常当天就有回复。") }}</p>
            <button type="button" class="btn btn--primary" style="margin-top:14px" @click="askAi('')">{{ $t("打开对话") }}</button>
          </div>
        </div>
      </div>
    </section>

    <section class="sec sec--tight" style="padding-top:0">
      <div class="wrap">
        <div class="cta">
          <div>
            <h2 class="cta__t">{{ $t("还没开店？先开一间再来问") }}</h2>
            <p class="cta__d">{{ $t("大部分问题，开完店自己就明白了。免费的，不绑卡。") }}</p>
          </div>
          <div class="cta__btns">
            <router-link class="btn btn--primary" to="/sign-up">{{ $t("免费开店") }}</router-link>
            <router-link class="btn btn--ghost" to="/feature">{{ $t("看功能") }}</router-link>
          </div>
        </div>
      </div>
    </section>

  </main>
</template>

<script>
import { openChat } from '@/utils/chat'
import { cats, faqs } from '@/data/helpFaq'

/**
 * 帮助中心
 * ------------------------------------------------------------------
 * 搜索是「本地 FAQ 过滤 + AI 客服兜底」两段式：
 *   有命中 → 只显示命中的 FAQ
 *   没命中 → 直接把关键词发给 AI 客服（右下角气泡自动打开并提问），
 *            访客不用再复制粘贴一遍
 * 分类卡也是筛选器，不是装饰性锚点。
 */
export default {
  name: 'WebsiteHelp',
  data () {
    return {
      q: '',
      cat: '',
      asked: '',
      // 分类与常见问题从 @/data/helpFaq 引入：放 data() 里会漏翻（抽取脚本只认 $t 字面量）
      cats,
      faqs,
    }
  },
  computed: {
    // 常搜词跟着语言走：搜的是页面上渲染出的文字
    hotWords () {
      return [this.$t('改价格'), this.$t('退款'), this.$t('绑定域名'), this.$t('忘记密码'), this.$t('物流不更新')]
    },
    catName () {
      const hit = this.cats.filter(c => c.key === this.cat)[0]
      return hit ? hit.name : ''
    },
    filtered () {
      const kw = String(this.q || '').trim().toLowerCase()
      return this.faqs.filter(f => {
        if (this.cat && f.cat !== this.cat) return false
        if (!kw) return true
        return (f.q + f.a).toLowerCase().indexOf(kw) >= 0
      })
    },
    hits () {
      return this.filtered.length
    },
  },
  methods: {
    countOf (key) {
      return this.faqs.filter(f => f.cat === key).length
    },
    pickCat (key) {
      this.cat = this.cat === key ? '' : key
      this.q = ''
      this.scrollToFaq()
    },
    pickHot (w) {
      this.q = w
      this.cat = ''
      this.doSearch()
    },
    resetFilter () {
      this.q = ''
      this.cat = ''
      this.asked = ''
    },
    /**
     * 搜索：先看本地 FAQ，命中就只显示命中项；
     * 一条都没命中，说明这问题不在帮助中心覆盖范围内，直接把它交给 AI 客服。
     */
    doSearch () {
      const kw = String(this.q || '').trim()
      this.asked = ''
      this.scrollToFaq()
      if (kw && !this.filtered.length) {
        this.asked = kw
        openChat(kw)
      }
    },
    scrollToFaq () {
      this.$nextTick(() => {
        const box = this.$refs.faq
        if (box && box.scrollIntoView) box.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    },
    askAi (question) {
      openChat(question || '')
    },
  },
}
</script>

<style scoped>
.hsearch__ai {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 640px;
  margin: 18px auto 0;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #ececf6;
  border-radius: 12px;
  text-align: left;
  box-shadow: 0 6px 20px rgba(24, 20, 60, .06);
}

.hsearch__ai-ico {
  width: 34px;
  height: 34px;
  flex: none;
  border-radius: 10px;
  background: #f4f2ff;
  color: #7331FF;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hsearch__ai-txt { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.hsearch__ai-txt b { font-size: 14px; color: #2C3740; font-weight: 600; }
.hsearch__ai-txt em { font-style: normal; font-size: 12.4px; color: #6B6580; margin-top: 2px; }

.hsearch__ai-btn {
  flex: none;
  border: 0;
  background: #7331FF;
  color: #fff;
  border-radius: 99px;
  padding: 8px 16px;
  font-size: 13px;
  cursor: pointer;
}

.hsearch__ai-btn:hover { background: #8a4dff; }

.hcat.is-on { border-color: #7331FF; box-shadow: 0 6px 18px rgba(115, 49, 255, .14); }

.hfaq__bar {
  max-width: 880px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  color: #6B6580;
}

.hfaq__bar button {
  border: 1px solid #ddd9fb;
  background: #f4f2ff;
  color: #7331FF;
  border-radius: 99px;
  padding: 4px 12px;
  font-size: 12.5px;
  cursor: pointer;
}

.hfaq__ask {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 10px;
  border: 0;
  background: transparent;
  color: #7331FF;
  font-size: 12.8px;
  cursor: pointer;
  padding: 0;
}

.hfaq__ask .ico { width: 15px; height: 15px; }
.hfaq__ask:hover { text-decoration: underline; }

.hfaq__empty {
  text-align: center;
  padding: 28px 16px;
  background: #fff;
  border: 1px dashed #ddd9fb;
  border-radius: 12px;
}

.hfaq__empty p { margin-bottom: 14px; color: #6B6580; font-size: 14px; }

.hfaq__sent {
  max-width: 880px;
  margin: 16px auto 0;
  font-size: 13px;
  color: #2FA96B;
}

@media (max-width: 600px) {
  .hsearch__ai { flex-wrap: wrap; }
  .hsearch__ai-btn { width: 100%; }
}
</style>
